import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { api, getSession, formatDate, formatTime, formatZar, type Booking } from "../lib/api";
import { Shell } from "../components/Shell";

export default function MyBookings() {
  const [, navigate] = useLocation();
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  // Highlight a freshly-created booking via ?new=<id>
  const newId = new URLSearchParams(window.location.search).get("new");

  useEffect(() => {
    if (!getSession()) {
      navigate("/login?next=/me/bookings");
      return;
    }
    api.myBookings().then(setBookings).catch(e => setErr(e.message));
  }, [navigate]);

  async function cancel(id: string) {
    if (!confirm("Cancel this booking?")) return;
    setCancelling(id);
    try {
      await api.cancelBooking(id);
      const fresh = await api.myBookings();
      setBookings(fresh);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCancelling(null);
    }
  }

  return (
    <Shell>
      <div className="mx-auto max-w-4xl px-6 py-16">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">My bookings</h1>
        <p className="mt-2 text-neutral-400">Tee times you’ve booked through Sportal.</p>

        {newId && (
          <div className="mt-6 rounded-xl border border-accent-500/30 bg-accent-500/10 p-5">
            <div className="font-medium text-accent-300">Booking confirmed</div>
            <div className="text-sm text-neutral-300 mt-1">
              Pay at the club on arrival. You’ll get a confirmation when the club reviews it.
            </div>
          </div>
        )}

        {err && <div className="mt-6 rounded-xl border border-red-900/50 bg-red-950/30 text-red-200 p-4">{err}</div>}

        {!bookings && !err && (
          <div className="mt-8 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 rounded-xl bg-neutral-900/50 border border-neutral-900 animate-pulse" />
            ))}
          </div>
        )}

        {bookings && bookings.length === 0 && (
          <div className="mt-8 rounded-2xl border border-neutral-800 bg-neutral-900/30 p-10 text-center">
            <p className="text-neutral-400">You haven’t booked any tee times yet.</p>
            <Link href="/clubs" className="mt-4 inline-flex items-center rounded-full bg-accent-500 text-neutral-950 px-5 py-2 text-sm font-medium hover:bg-accent-400 transition-colors">
              Find a tee time
            </Link>
          </div>
        )}

        {bookings && bookings.length > 0 && (
          <div className="mt-8 space-y-3">
            {bookings.map(b => (
              <div
                key={b.id}
                className={
                  "rounded-2xl border p-5 sm:p-6 " +
                  (b.id === newId
                    ? "border-accent-500/50 bg-neutral-900/50"
                    : "border-neutral-800 bg-neutral-900/30")
                }
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-neutral-500">
                      {b.club?.name ?? "Club"} · {b.club?.city ?? ""}
                    </div>
                    <div className="mt-1 text-xl font-medium tabular-nums">
                      {b.slot ? `${formatTime(b.slot.start_time)} · ${formatDate(b.slot.start_time)}` : "—"}
                    </div>
                    <div className="mt-2 text-sm text-neutral-400">
                      {b.players} {b.players === 1 ? "player" : "players"} · {formatZar(b.total_cents)} · pay at club
                    </div>
                    {b.guest_names && b.guest_names.length > 0 && (
                      <div className="mt-1 text-sm text-neutral-500">With: {b.guest_names.join(", ")}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-3">
                    <StatusBadge status={b.status} />
                    {(b.status === "pending" || b.status === "confirmed") && (
                      <button
                        disabled={cancelling === b.id}
                        onClick={() => cancel(b.id)}
                        className="text-xs text-neutral-400 hover:text-red-300 disabled:opacity-50 transition-colors"
                      >
                        {cancelling === b.id ? "Cancelling…" : "Cancel booking"}
                      </button>
                    )}
                  </div>
                </div>
                {b.club?.contact_phone && (
                  <div className="mt-4 pt-4 border-t border-neutral-900 text-xs text-neutral-500">
                    Club contact: <a href={`tel:${b.club.contact_phone}`} className="text-neutral-300 hover:text-neutral-0">{b.club.contact_phone}</a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-300" },
    confirmed: { label: "Confirmed", cls: "bg-accent-500/15 text-accent-300" },
    checked_in: { label: "Checked in", cls: "bg-blue-500/15 text-blue-300" },
    completed: { label: "Played", cls: "bg-neutral-800 text-neutral-300" },
    cancelled: { label: "Cancelled", cls: "bg-neutral-800 text-neutral-400" },
    no_show: { label: "No-show", cls: "bg-red-500/15 text-red-300" },
  };
  const m = map[status] ?? { label: status, cls: "bg-neutral-800 text-neutral-300" };
  return <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${m.cls}`}>{m.label}</span>;
}
